import { Link } from "react-router-dom";
import { Heart, MapPin, BedDouble, Bath, Car, Maximize, ShieldCheck, GitCompare, Zap, TrendingDown } from "lucide-react";
import { formatPrice, typeLabel, assessmentStyle, scoreColor } from "../lib/format";
import { fileUrl } from "../lib/api";
import { useApp } from "../context/AppContext";

export default function PropertyCard({ p }) {
  const { toggleSave, isSaved, toggleCompare, inCompare } = useApp();
  const img = fileUrl(p.images?.[0]) || "https://images.unsplash.com/photo-1564078516393-cf04bd966897?w=800&q=80";

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden flex flex-col" data-testid={`property-card-${p.property_id}`}>
      <div className="relative">
        <Link to={`/properties/${p.slug}`}>
          <div className="aspect-[16/10] overflow-hidden bg-slate-100">
            <img src={img} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
        </Link>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {p.personally_verified && (
            <span className="inline-flex items-center gap-1 bg-indigo-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
              <ShieldCheck className="w-3 h-3" /> Personally Verified
            </span>
          )}
          {p.urgent_sale && <span className="inline-flex items-center gap-1 bg-rose-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"><Zap className="w-3 h-3" /> Urgent Sale</span>}
          {p.price_reduced && <span className="inline-flex items-center gap-1 bg-amber-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"><TrendingDown className="w-3 h-3" /> Price Reduced</span>}
        </div>
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          <button onClick={() => toggleSave(p.id)} data-testid={`save-btn-${p.property_id}`}
            className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-colors ${isSaved(p.id) ? "bg-rose-500 text-white" : "bg-white/90 text-slate-600 hover:text-rose-500"}`}>
            <Heart className="w-4 h-4" fill={isSaved(p.id) ? "currentColor" : "none"} />
          </button>
          <button onClick={() => toggleCompare(p.id)} data-testid={`compare-btn-${p.property_id}`}
            className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-colors ${inCompare(p.id) ? "bg-indigo-700 text-white" : "bg-white/90 text-slate-600 hover:text-indigo-700"}`}>
            <GitCompare className="w-4 h-4" />
          </button>
        </div>
        {p.score_overall ? (
          <div className="absolute bottom-3 right-3 bg-white rounded-xl px-2.5 py-1 shadow-md text-center">
            <div className={`font-heading font-extrabold text-base leading-none ${scoreColor(p.score_overall)}`}>{p.score_overall}</div>
            <div className="text-[9px] text-slate-500 uppercase tracking-wide">Score</div>
          </div>
        ) : null}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
          <MapPin className="w-3.5 h-3.5 text-indigo-700" /> {p.sector}, Faridabad
        </div>
        <Link to={`/properties/${p.slug}`}>
          <h3 className="font-heading font-bold text-slate-900 text-lg leading-snug hover:text-indigo-800 line-clamp-1">{p.title}</h3>
        </Link>
        <div className="text-[11px] text-slate-400 uppercase tracking-wide mt-0.5">{typeLabel(p.property_type)} · {p.property_id}</div>

        <div className="flex items-end justify-between mt-3">
          <div className="font-heading font-extrabold text-indigo-900 text-xl">{formatPrice(p.price)}</div>
          {p.area_sqft ? <div className="text-sm text-slate-500">{p.area_sqft.toLocaleString("en-IN")} sq.ft</div> : null}
        </div>

        <div className="flex items-center gap-4 mt-3 text-sm text-slate-600 border-t border-slate-100 pt-3">
          {p.bedrooms ? <span className="flex items-center gap-1"><BedDouble className="w-4 h-4 text-slate-400" /> {p.bedrooms}</span> : null}
          {p.bathrooms ? <span className="flex items-center gap-1"><Bath className="w-4 h-4 text-slate-400" /> {p.bathrooms}</span> : null}
          {p.parking ? <span className="flex items-center gap-1"><Car className="w-4 h-4 text-slate-400" /> {p.parking}</span> : null}
          {p.area_sqft ? <span className="flex items-center gap-1"><Maximize className="w-4 h-4 text-slate-400" /> {p.area_sqft}</span> : null}
        </div>

        <div className="mt-3 flex items-center justify-between">
          {p.price_assessment ? (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${assessmentStyle(p.price_assessment)}`}>{p.price_assessment}</span>
          ) : <span />}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Link to={`/properties/${p.slug}`} data-testid={`view-details-${p.property_id}`}>
            <button className="w-full text-sm font-medium py-2 rounded-xl border border-slate-200 text-slate-700 hover:border-indigo-700 hover:text-indigo-800 transition-colors">View Details</button>
          </Link>
          <Link to={`/book-site-visit?property=${p.id}`} data-testid={`book-visit-${p.property_id}`}>
            <button className="w-full text-sm font-medium py-2 rounded-xl bg-indigo-900 text-white hover:bg-indigo-800 transition-colors">Book Site Visit</button>
          </Link>
        </div>
      </div>
    </div>
  );
}
